import { Component, effect, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GOAL_TYPE, GoalDescriptor, GoalNamePipe, goalDescriptor } from './ft-goals-panel.component';

interface GoalVariation {
    name: string;
    identifier: number;
    type: number;
}

/**
 * Native port of the AngularJS `ftGoalSelector` directive — the goal-type + goal-limit
 * dropdown pair inside each goal row of the friends-goals editor. `availableGoals`,
 * `descriptor` and the toggle label are re-derived imperatively in the input setters and
 * after `setGoal`/`setLimit`, because the parent mutates the same goal object in place
 * (an `ngOnChanges` won't fire).
 */
@Component({
    selector: 'ft-goal-selector-ng',
    imports: [],
    providers: [GoalNamePipe],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-goal-selector.component.html',
    host: {
        '(document:click)': 'onDocumentClick()',
    }
})
export class FtGoalSelectorComponent {
    /** The goal slot ({ type, clicks, signups, … }); mutated in place on setGoal/setLimit. */
    readonly goal = input<any>();

    /** The allowed goal-type numbers (v1 `types`); UNDEFINED on the contest host → no menu. */
    readonly types = input<any>();

    /** v1 `model.availableGoals` — the variations whose `type` is in `types`. */
    protected readonly availableGoals = signal<GoalVariation[]>([]);
    /** v1 `model.goalLimits = _.range(1, 21)` → [1..20]. */
    protected readonly goalLimits = Array.from({ length: 20 }, (_v, i) => i + 1);
    /** v1 `model.descriptor = goalDescriptor(goal)`. */
    protected readonly descriptor = signal<GoalDescriptor | undefined>(undefined);
    /** v1 toggle label `{{goal | goalName}}` — imperative (the pure pipe wouldn't refresh). */
    protected readonly goalLabel = signal('');
    protected readonly numbersOpen = signal(false);
    protected readonly typesOpen = signal(false);

    private readonly translate = inject(TranslateService);
    private readonly goalNamePipe = inject(GoalNamePipe);
    /** v1 `allGoalsVariations` (built once at link). */
    private readonly allGoalsVariations: GoalVariation[];

    constructor() {
        const t = (key: string) => this.translate.instant(key);
        this.allGoalsVariations = [
            { name: t('flipto.components.goals.SpreadTheWord'), identifier: GOAL_TYPE['SpreadTheWord'], type: 1 },
            { name: t('flipto.components.goals.GetSocialConnections'), identifier: GOAL_TYPE['GetSocialConnections'], type: 1 },
            { name: t('flipto.components.goals.GetUniqueVisitors'), identifier: GOAL_TYPE['GetUniqueVisitors'], type: 1 },
            { name: t('flipto.components.goals.SignupAndGet'), identifier: GOAL_TYPE['SignupAndGet'], type: 2 },
            { name: t('flipto.components.goals.ShareAndGet'), identifier: GOAL_TYPE['ShareAndGet'], type: 4 },
            { name: t('flipto.components.goals.SubmitQuoteAndGet'), identifier: GOAL_TYPE['SubmitQuoteAndGet'], type: 3 },
            { name: t('flipto.components.goals.PhotoContestWinner'), identifier: GOAL_TYPE['PhotoContestWinner'], type: 5 },
            { name: t('flipto.components.goals.SubmitPhotoAndGet'), identifier: GOAL_TYPE['SubmitPhotoAndGet'], type: 6 },
            { name: t('flipto.components.goals.AttendeeSignup'), identifier: GOAL_TYPE['AttendeeSignup'], type: 7 },
        ];
        effect(() => this.recompute());
    }

    /** v1 `setGoal` — apply the picked variation onto the goal, then re-derive. */
    protected setGoal(variation: GoalVariation): void {
        const goal = this.goal();
        if (!goal) return;
        goal.type = variation.type;
        goal.signups = 0;
        goal.clicks = 0;
        if (variation.identifier === GOAL_TYPE['GetSocialConnections']) {
            goal.signups = 1;
        } else if (variation.identifier === GOAL_TYPE['GetUniqueVisitors']) {
            goal.clicks = 1;
        }
        this.recompute();
    }

    /** v1 `setLimit` — write the limit onto clicks/signups per the current descriptor, then re-derive. */
    protected setLimit(limit: number): void {
        const goal = this.goal();
        const descriptor = this.descriptor();
        if (!goal || !descriptor) return;
        if (descriptor.type === GOAL_TYPE['GetUniqueVisitors']) {
            goal.clicks = limit;
        } else if (descriptor.type === GOAL_TYPE['GetSocialConnections']) {
            goal.signups = limit;
        }
        this.recompute();
    }

    /** v1 `goalHasLimit` — only the Advocate visitor/connection variations carry a count. */
    protected goalHasLimit(goal: any): boolean {
        if (!goal) return false;
        const descriptor = goalDescriptor(goal);
        return goal.type == 1 && !!descriptor && descriptor.type !== GOAL_TYPE['SpreadTheWord'];
    }

    protected toggleNumbers(event: Event): void {
        event.stopPropagation();
        this.numbersOpen.update((open) => !open);
        this.typesOpen.set(false);
    }

    protected toggleTypes(event: Event): void {
        event.stopPropagation();
        this.typesOpen.update((open) => !open);
        this.numbersOpen.set(false);
    }

    protected onDocumentClick(): void {
        this.numbersOpen.set(false);
        this.typesOpen.set(false);
    }

    /** v1 deep `$watch("goal")` body — re-derive availableGoals + descriptor + the toggle label. */
    private recompute(): void {
        const goal = this.goal();
        const types = this.types();
        if (!goal) return;
        this.availableGoals.set(
            this.allGoalsVariations.filter((variation) =>
                Array.isArray(types) ? types.some((type: any) => type == variation.type) : false,
            ),
        );
        this.descriptor.set(goalDescriptor(goal));
        this.goalLabel.set(this.goalNamePipe.transform(goal));
    }
}
